import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from '@/shared/hooks/use-media-query'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet'
import { shouldCloseSidePanelFromKeydown } from './side-panel-escape'
import { SidePanelFooter } from './side-panel-footer'
import { SidePanelHeader } from './side-panel-header'
import { getSidePanelDefinition } from './side-panel-registry'
import { SIDE_PANEL_WIDTH } from './side-panel-types'

const GAP = 8

export function SidePanel() {
  const {
    isOpen,
    currentPage,
    params,
    navigationStack,
    width,
    content,
    title,
    icon,
    footer,
    closePanel,
    cleanupClosedPanel,
    goBack,
    setWidth
  } = useSidePanelStore()
  const isDesktop = useMediaQuery('md')
  const [shouldRender, setShouldRender] = useState(false)
  const [isResizing, setIsResizing] = useState(false)

  const registryEntry = currentPage ? getSidePanelDefinition(currentPage) : null
  const panelTitle = registryEntry && params ? registryEntry.title(params as never) : title
  const panelIcon = registryEntry?.icon ?? icon
  const panelContent = registryEntry && params ? registryEntry.render(params as never) : content
  const panelFooter = registryEntry && params ? registryEntry.footer?.(params as never) : footer
  const canGoBack = navigationStack.length > 1

  useEffect(() => {
    if (isOpen) setShouldRender(true)
  }, [isOpen])

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      // Los handlers React alcanzan a marcar `defaultPrevented`; Radix no, porque su listener de
      // `document` corre después de éste. Las capas Radix anuncian la supresión en el store al
      // abrirse, así el primer Escape cierra sólo la capa superior y NUNCA el panel que la aloja.
      if (
        shouldCloseSidePanelFromKeydown(e, useSidePanelStore.getState().isEscapeCloseSuppressed)
      ) {
        closePanel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, closePanel])

  useEffect(() => {
    if (!isResizing) return

    const handlePointerMove = (event: PointerEvent) => {
      const nextWidth = window.innerWidth - event.clientX
      setWidth(nextWidth)
    }

    const handlePointerUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp, { once: true })

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, setWidth])

  const header = useMemo(
    () => (
      <SidePanelHeader
        title={panelTitle}
        onClose={closePanel}
        icon={panelIcon}
        canGoBack={canGoBack}
        onBack={goBack}
      />
    ),
    [canGoBack, closePanel, goBack, panelIcon, panelTitle]
  )

  // Mobile: Sheet overlay
  if (!isDesktop) {
    return (
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) closePanel()
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col p-0 sm:w-[400px]"
          onAnimationEnd={(event) => {
            if (event.target !== event.currentTarget) return
            if (event.currentTarget.dataset.state === 'closed') cleanupClosedPanel()
          }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{panelTitle}</SheetTitle>
          </SheetHeader>
          {header}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{panelContent}</div>
          {panelFooter && <SidePanelFooter>{panelFooter}</SidePanelFooter>}
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop: animated flex sibling
  return (
    <motion.div
      className="shrink-0 overflow-hidden"
      initial={false}
      animate={{ width: isOpen ? width + GAP : 0 }}
      transition={{ duration: isResizing ? 0 : 0.2, ease: [0.4, 0, 0.2, 1] }}
      onAnimationComplete={() => {
        if (!isOpen) {
          setShouldRender(false)
          cleanupClosedPanel()
        }
      }}
    >
      {shouldRender && (
        <div
          className={cn(
            'relative ml-2 flex h-full flex-col overflow-hidden rounded-xl border border-accent bg-background',
            isOpen && 'shadow-sm'
          )}
          style={{ width }}
        >
          <button
            type="button"
            aria-label="Redimensionar panel"
            className="absolute left-0 top-0 z-20 h-full w-1 cursor-col-resize touch-none hover:bg-primary/20"
            onPointerDown={(event) => {
              event.preventDefault()
              setIsResizing(true)
            }}
          />
          {header}
          <div className="min-h-0 flex-1 overflow-y-auto">{panelContent}</div>
          {panelFooter && <SidePanelFooter>{panelFooter}</SidePanelFooter>}
          <span className="sr-only">
            Ancho del panel entre {SIDE_PANEL_WIDTH.min} y {SIDE_PANEL_WIDTH.max} pixeles.
          </span>
        </div>
      )}
    </motion.div>
  )
}
